<template>
  <div class="max-w-3xl pb-20 px-2 lg:px-4">
    <!-- Selection Dialog -->
    <teleport to="body">
      <md-dialog 
        ref="dialogRef" 
        class="md-dialog-custom"
        quick
      >
        <div slot="headline">{{ currentSelectionTitle }}</div>
        <div slot="content">
          <md-list class="dialog-list">
            <md-list-item 
              v-for="opt in currentOptions" 
              :key="opt.value" 
              type="button" 
              @click="selectAndClose(opt.value)"
            >
              <div slot="headline">{{ opt.label }}</div>
              <md-icon slot="end" v-if="settingsStore[activeKey] === opt.value" class="text-primary"><IconCheck /></md-icon>
            </md-list-item>
          </md-list>
        </div>
        <div slot="actions">
          <md-text-button @click="dialogRef.close()">{{ $t('forecastInfo.close') }}</md-text-button>
        </div>
      </md-dialog>
    </teleport>

    <!-- Forecast Category -->
    <h3 class="category-header">{{ $t('settings.forecast') }}</h3>
    <md-list class="settings-card shadow-sm">
      <md-list-item type="button" @click="openSelection('provider', $t('settings.data_source'), options.provider)">
        <div slot="headline">{{ $t('settings.data_source') }}</div>
        <div slot="supporting-text">{{ getLabel('provider') }}</div>
      </md-list-item>

      <md-divider></md-divider>

      <md-list-item type="button" @click="toggleSwitch('hideEarlyMorning')">
        <div slot="headline">{{ $t('settings.hide_early_morning') }}</div>
        <div slot="supporting-text" class="pr-12">{{ $t('settings.hide_early_morning_explanation') }}</div>
        <md-switch slot="end" :selected="settingsStore.hideEarlyMorning" @click.stop @change="updateSetting('hideEarlyMorning', $event.target.selected)"></md-switch>
      </md-list-item>

      <md-divider></md-divider>

      <md-list-item type="button" @click="toggleSwitch('showMinMax')">
        <div slot="headline">{{ $t('settings.show_minMax') }}</div>
        <div slot="supporting-text" class="pr-12">{{ $t('settings.show_minMax_summary') }}</div>
        <md-switch slot="end" :selected="settingsStore.showMinMax" @click.stop @change="updateSetting('showMinMax', $event.target.selected)"></md-switch>
      </md-list-item>
    </md-list>

    <!-- Units Category -->
    <h3 class="category-header">{{ $t('settings.units') }}</h3>
    <md-list class="settings-card shadow-sm">
       <md-list-item type="button" @click="openSelection('temperatureUnits', $t('settings.temperature_units'), options.temperatureUnits)">
        <div slot="headline">{{ $t('settings.temperature_units') }}</div>
        <div slot="supporting-text">{{ getLabel('temperatureUnits') }}</div>
      </md-list-item>

       <md-divider></md-divider>

       <md-list-item type="button" @click="openSelection('windSpeedUnits', $t('settings.wind_speed_units'), options.windSpeedUnits)">
        <div slot="headline">{{ $t('settings.wind_speed_units') }}</div>
        <div slot="supporting-text">{{ getLabel('windSpeedUnits') }}</div>
      </md-list-item>

       <md-divider></md-divider>

       <md-list-item type="button" @click="openSelection('precipitationUnits', $t('settings.precipitation_units'), options.precipitationUnits)">
        <div slot="headline">{{ $t('settings.precipitation_units') }}</div>
        <div slot="supporting-text">{{ getLabel('precipitationUnits') }}</div>
      </md-list-item>
    </md-list>


    <!-- Appearance Category -->
    <h3 class="category-header">{{ $t('settings.appearance') }}</h3>
    <md-list class="settings-card shadow-sm">
       <md-list-item type="button" @click="openSelection('theme', $t('settings.theme'), options.theme)">
        <div slot="headline">{{ $t('settings.theme') }}</div>
        <div slot="supporting-text">{{ getLabel('theme') }}</div>
      </md-list-item>

       <md-divider></md-divider>

       <md-list-item type="button" @click="openSelection('locale', $t('settings.language'), options.locale)">
        <div slot="headline">{{ $t('settings.language') }}</div>
        <div slot="supporting-text">{{ getLabel('locale') }}</div>
      </md-list-item>
    </md-list>

    <!-- About Category -->
    <h3 class="category-header">{{ $t('settings.about') }}</h3>
     <md-list class="settings-card shadow-sm">
      <md-list-item type="link" href="/legal/privacy-policy" target="_blank">
        <div slot="headline">{{ $t('settings.privacy_policy') }}</div>
        <md-icon slot="end"><IconOpenInNew /></md-icon>
      </md-list-item>
      
      <md-divider></md-divider>

      <router-link :to="{ name: 'TermsOfService' }" custom v-slot="{ href, navigate }">
        <md-list-item type="link" :href="href" @click="navigate">
          <div slot="headline">{{ $t('settings.terms_of_service') }}</div>
          <md-icon slot="end"><IconOpenInNew /></md-icon>
        </md-list-item>
      </router-link>
    </md-list>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSettingsStore } from '../stores/settings';
import { getSettingsOptions } from '../constants/settingsOptions';

// Import Icons
import IconOpenInNew from '~icons/material-symbols/open-in-new';
import IconCheck from '~icons/material-symbols/check-rounded';

// Import Material Web Components
import '@material/web/list/list.js';
import '@material/web/list/list-item.js';
import '@material/web/divider/divider.js';
import '@material/web/icon/icon.js';
import '@material/web/switch/switch.js';
import '@material/web/dialog/dialog.js';
import '@material/web/button/text-button.js';

const settingsStore = useSettingsStore();
const { t } = useI18n();
const options = computed(() => getSettingsOptions(t));

// Selection Dialog Logic
const dialogRef = ref(null);
const activeKey = ref('');
const currentSelectionTitle = ref('');
const currentOptions = ref([]);

const openSelection = (key, title, opts) => {
  activeKey.value = key;
  currentSelectionTitle.value = title;
  currentOptions.value = opts;
  dialogRef.value.show();
};

const selectAndClose = (value) => {
  updateSetting(activeKey.value, value);
  dialogRef.value.close();
};

const getLabel = (key) => {
  const currentVal = settingsStore[key];
  const option = options.value[key]?.find(o => o.value === currentVal);
  return option ? option.label : '';
};

const updateSetting = (key, value) => {
  settingsStore.setSetting(key, value);
};

const toggleSwitch = (key) => {
  settingsStore.setSetting(key, !settingsStore[key]);
};
</script>

<style scoped>
@reference "../assets/main.css";

.category-header {
  color: var(--color-rain);
  opacity: 0.9;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 1.5rem 1rem 0.5rem 1rem;
}

.settings-card {
  --md-list-container-color: var(--color-surface);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 0.5rem;
  padding: 0;
  background: var(--color-surface);
}

/* Tabs as Segmented Buttons */
.compact-tabs {
  --md-tabs-container-height: 32px;
  --md-tabs-active-indicator-color: var(--color-rain);
  --md-tabs-active-label-text-color: var(--color-rain);
  --md-tabs-divider-color: transparent;
  min-width: 160px;
}

/* Dialog Styling */
.md-dialog-custom {
  max-width: 400px;
  width: 90vw;
}



.dialog-list {
  background: transparent;
}

.dialog-list md-list-item {
  --md-list-item-one-line-container-height: 52px;
}



.tab-label {
  font-weight: 600;
}

[slot="headline"] {
  font-weight: 500;
}
</style>

